import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAiAccuracy } from '../hooks/useAiAccuracy';
import type { AccuracyComparison } from '../types';

type PathToken = string | number | '__length__';

function parseDiffPath(path: string): PathToken[] {
  if (!path || path === '$') return [];
  const tokens: PathToken[] = [];
  const regex = /([^.[\]]+)|\[(\d+|length)\]/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(path))) {
    if (m[1] !== undefined) tokens.push(m[1]);
    else if (m[2] === 'length') tokens.push('__length__');
    else tokens.push(Number(m[2]));
  }
  return tokens;
}

function getAtPath(root: unknown, tokens: PathToken[]): unknown {
  let cur: unknown = root;
  for (const t of tokens) {
    if (cur == null) return undefined;
    if (t === '__length__') {
      return Array.isArray(cur) ? cur.length : undefined;
    }
    cur = (cur as Record<string | number, unknown>)[t as string | number];
  }
  return cur;
}

function formatValue(v: unknown): string {
  if (v === undefined) return '—';
  if (v === null) return 'null';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function tryParseJson(raw: string): { ok: true; value: unknown } | { ok: false } {
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch {
    return { ok: false };
  }
}

function DiffView({ comparison }: { comparison: AccuracyComparison }) {
  if (comparison.matched) {
    return <div className="text-sm text-gray-500 italic">No diff.</div>;
  }

  const suggested = tryParseJson(comparison.aiSuggestedPayload);
  const actual = tryParseJson(comparison.actualPayload);

  if (!suggested.ok || !actual.ok) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs space-y-1">
        <div>
          <span className="font-semibold text-gray-700">AI:</span>{' '}
          <span className="font-mono">{comparison.aiSuggestedPayload}</span>
        </div>
        <div>
          <span className="font-semibold text-gray-700">Actual:</span>{' '}
          <span className="font-mono">{comparison.actualPayload}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-200 rounded">
      <table className="text-xs w-full">
        <thead className="bg-gray-50">
          <tr className="text-left text-gray-600">
            <th className="py-1.5 px-3 font-semibold">Path</th>
            <th className="py-1.5 px-3 font-semibold">AI suggested</th>
            <th className="py-1.5 px-3 font-semibold">Actual</th>
          </tr>
        </thead>
        <tbody>
          {comparison.diffPaths.map((p) => {
            const tokens = parseDiffPath(p);
            const s = getAtPath(suggested.value, tokens);
            const a = getAtPath(actual.value, tokens);
            return (
              <tr key={p} className="border-t border-gray-100 align-top">
                <td className="py-1.5 px-3 font-mono text-gray-700 whitespace-nowrap">
                  {p}
                </td>
                <td className="py-1.5 px-3 font-mono text-red-700 break-all">
                  {formatValue(s)}
                </td>
                <td className="py-1.5 px-3 font-mono text-green-700 break-all">
                  {formatValue(a)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ComparisonRow({ comparison }: { comparison: AccuracyComparison }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li className="py-4">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between text-left hover:bg-gray-50 rounded px-2 -mx-2 py-1"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              comparison.matched
                ? 'bg-green-100 text-green-800'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            {comparison.matched ? 'Match' : 'Mismatch'}
          </span>
          <Link
            to={`/batch/${comparison.date}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-sm text-blue-600 hover:text-blue-800 truncate"
          >
            {comparison.orderId}
          </Link>
          {comparison.aiConfidence && (
            <span className="text-xs text-gray-500">
              confidence: {comparison.aiConfidence}
            </span>
          )}
          {!comparison.matched && (
            <span className="text-xs text-gray-500">
              {comparison.diffPaths.length} diff{comparison.diffPaths.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
          <span>{new Date(comparison.recordedAt).toLocaleString()}</span>
          <span>{expanded ? '▼' : '▶'}</span>
        </div>
      </button>

      {expanded && (
        <div className="mt-3">
          <DiffView comparison={comparison} />
        </div>
      )}
    </li>
  );
}

export function AiAccuracy() {
  const { data, isLoading, error } = useAiAccuracy(10);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          AI Suggestion Accuracy{' '}
          <span className="text-amber-600 text-sm font-medium align-middle">
            (Experimental)
          </span>
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Compares AI-suggested export payloads against the actual payload used
          when an order reaches a completed state.
        </p>
      </div>

      {isLoading && (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500">
          Loading…
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700">
          Failed to load accuracy data: {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-sm text-gray-500">Overall accuracy</div>
              <div className="mt-2 text-3xl font-bold text-gray-900">
                {data.summary.accuracyPct === null
                  ? '—'
                  : `${data.summary.accuracyPct}%`}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-sm text-gray-500">Matches</div>
              <div className="mt-2 text-3xl font-bold text-green-600">
                {data.summary.matches}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-sm text-gray-500">Total comparisons</div>
              <div className="mt-2 text-3xl font-bold text-gray-900">
                {data.summary.total}
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Latest comparisons
              </h2>
            </div>
            <div className="px-6">
              {data.recent.length === 0 ? (
                <p className="py-6 text-sm text-gray-500">
                  No comparisons recorded yet. A comparison is captured when an
                  order with an AI-suggested payload auto-resolves to
                  "exported".
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {data.recent.map((c) => (
                    <ComparisonRow
                      key={`${c.recordedAt}#${c.orderId}`}
                      comparison={c}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
